import { SplatMeshRenderer } from "@/components/r3f/renderers/splat-mesh-renderer";
import { GaussianSplatsObject } from "@/object-cache";
import { useThree, type ThreeEvent } from "@react-three/fiber";
import { JSX, useCallback } from "react";
import { type Vector3 } from "three";
import type { MoveCameraToCbk } from "../data-dispatch/use-move-camera-to";
import { GUIDED_NAVIGATION_PARAMS } from "../tools/guided-navigation/guided-navigation-params";
import { useToolsAcquaintance } from "../tools/tools-acquaintance-context";

type FlyWalkGaussianSplatsRendererProps = {
  /** The current active gaussian splats */
  gaussianSplats: GaussianSplatsObject | null;
  /** Callback issued when a point on the floor in the gaussian splats model is clicked */
  onWalkToPosition?: MoveCameraToCbk;
};

/** @returns a wrapper component to instantiate or not the gaussian splats renderer */
export function FlyWalkGaussianSplatsRenderer({
  gaussianSplats,
  onWalkToPosition,
}: FlyWalkGaussianSplatsRendererProps): JSX.Element | null {
  const {
    clippingPlanes,
    activeToolAndPicking,
    guidedNavigation,
    isGuidedNavigationActive,
  } = useToolsAcquaintance();

  const camera = useThree((s) => s.camera);

  // When a gaussian splat is clicked, see if the camera can navigate there
  // (walk and splitscreen mode only)
  const onPointClicked = useCallback(
    (point: Vector3) => {
      // Do not walk to the point is there is no walk-to callback
      // or if there is a picking tool active
      if (!onWalkToPosition || activeToolAndPicking.isPickingToolActive) {
        return;
      }
      const targetPosition = point.clone();
      if (isGuidedNavigationActive) {
        // Any point is allowed, as the destination is moved inside the trajectory tunnels
        targetPosition.y += GUIDED_NAVIGATION_PARAMS.cameraHeight;
        guidedNavigation.clampToTunnel(targetPosition);
      } else {
        // Skip points that are reasonably not on the floor
        if (point.y > camera.position.y || point.y < camera.position.y - 2) {
          return;
        }
        targetPosition.y = camera.position.y;
      }
      onWalkToPosition(targetPosition);
    },
    [
      onWalkToPosition,
      activeToolAndPicking,
      camera,
      guidedNavigation,
      isGuidedNavigationActive,
    ],
  );

  const onClick = useCallback(
    (ev: ThreeEvent<MouseEvent>) => {
      if (ev.delta > 5) return;
      onPointClicked(ev.point);
    },
    [onPointClicked],
  );

  if (!gaussianSplats) {
    return null;
  }
  return (
    <SplatMeshRenderer
      splatMesh={gaussianSplats}
      clippingPlanes={clippingPlanes}
      onClick={onClick}
    />
  );
}
